import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { IndividualPaymentParcelSaveViewModel } from '@/shared/api/generated';
import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey,
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  usePutApiV1IndividualpaymentParcelsParcelid,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * Edição de uma parcela de um contrato financeiro.
 *
 * As mesmas duas queries da criação são invalidadas no sucesso, e pelos mesmos
 * motivos: a lista de parcelas porque a linha alterada só existe do lado do
 * servidor — a tela nunca monta a parcela editada localmente —, e o header porque
 * seus totais são somas das parcelas.
 *
 * A classificação do erro é a mesma das demais escritas da feature
 * (`classifyPlanWriteError`): o `400` traz regra de negócio que o operador precisa
 * ler — Nosso Número em conflito com outra parcela ou com o histórico de boletos
 * do banco — e o `5xx` não tem tratamento global, então precisa ser sinalizado
 * pela tela.
 *
 * O endpoint tem efeitos colaterais declarados no contrato: alterar multa,
 * pontualidade, valor, vencimento, tipo ou número devolve a parcela para
 * "aguardando integração", e trocar a forma de pagamento zera a data de
 * integração. Nada disso é simulado no cliente — a invalidação traz o estado real.
 *
 * @param financialPersonId - ID do contrato, para as invalidações; `null` torna o
 *   `update` inerte.
 * @param parcelId - ID da parcela editada; `null` torna o `update` inerte.
 * @returns `{ update, isPending, isForbidden, isServerError, error, reset }`
 *
 * @example
 * ```tsx
 * const { update, isPending } = useUpdateParcel(101, 42);
 * update(payload, { onSuccess: () => navigate({ to: '..' }) });
 * ```
 */
export function useUpdateParcel(financialPersonId: number | null, parcelId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePutApiV1IndividualpaymentParcelsParcelid();

  const update = useCallback(
    (
      data: IndividualPaymentParcelSaveViewModel,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (financialPersonId === null || parcelId === null) return;

      mutation.mutate(
        { parcelId, data },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey:
                getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(
                  financialPersonId,
                ),
            });
            void queryClient.invalidateQueries({
              queryKey:
                getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey(
                  financialPersonId,
                ),
            });
            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [financialPersonId, parcelId, mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    update,
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Falha de regra de negócio, com a mensagem do backend quando houver. */
    error: failure,
    isForbidden: failure.kind === 'forbidden',
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
